import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import moment from 'moment/moment';
import { filter, map, Observable, Subject, take } from 'rxjs';
import { environment } from '../../../environments/environment';
import { Employee } from '../models/employee';
import { EmployeeMini } from '../models/employee-mini';
import { PaginationResult } from '../models/pagination-result';
import { AssignableEmployee } from '../models/assignable-employee';
import { Promotion } from '../models/promotion';
import { PromotionInterview } from '../models/promotion-interview';
import { EmployeeWorklog } from '../models/employee-worklog';
import { file } from 'jszip';

export interface EmployeeCurrentSalary {
  employeeId: number;
  grossSalary: number | null;
  netSalary: number | null;
  currency: string | null;
  annualSalary: number | null;
}

/** Staff pages with their own visibility rules (backend StaffVisibilityService.StaffView). */
export type StaffView = 'STAFF_LIST' | 'ATTENTION';

/** Mirrors the backend StaffVisibilityDto. */
export interface StaffVisibility {
  /** True when the user sees every employee and may pick any practice (or none). */
  allPractices: boolean;
  /** The user's own practice; the only one shown when allPractices is false. */
  practice: string | null;
}

@Injectable({ providedIn: 'root' })
export class EmployeeService {
  readonly defaultImageUrl = 'https://placehold.co/512';
  readonly defaultUrl = `${environment.apiUrl}/employees`;

  private _employeeTechChanged$ = new Subject<{ employeeId: number; technologies: string }>();
  employeeTechChanged$ = this._employeeTechChanged$.asObservable();
  emitEmployeeTechChanged(employeeId: number, technologies: string) {
    this._employeeTechChanged$.next({ employeeId, technologies });
  }

  private _employeeDatesChanged$ = new Subject<{
    employeeId: number;
    startDate: string | null;
    endDate: string | null;
  }>();
  employeeDatesChanged$ = this._employeeDatesChanged$.asObservable();
  emitEmployeeDatesChanged(employeeId: number, startDate: string | null, endDate: string | null) {
    this._employeeDatesChanged$.next({ employeeId, startDate, endDate });
  }

  private refreshEmployeeList$ = new Subject<void>();

  public get onEmployeeListRefresh$(): Observable<void> {
    return this.refreshEmployeeList$.asObservable();
  }

  public triggerEmployeeListRefresh(): void {
    this.refreshEmployeeList$.next();
  }
  constructor(private http: HttpClient) {}

  importEmployees(file: File): Observable<string> {
    const formData = new FormData();
    formData.append('file', file, file.name);
    return this.http.post(`${environment.apiUrl}/import-data/import-new-employee`, formData, {
      responseType: 'text' as 'json',
    }) as unknown as Observable<string>;
  }

  importBulkEmployees(file: File): Observable<any> {
    const formData = new FormData();
    formData.append('file', file, file.name);
    return this.http.post(`${environment.apiUrl}/import-data/import-employee-file`, formData);
  }

  importSalaries(file: File, allowInactive = false): Observable<string> {
    const formData = new FormData();
    formData.append('file', file, file.name);
    return this.http.post(`${environment.apiUrl}/import-data/import-salaries-file`, formData, {
      params: { allowInactive },
      responseType: 'text' as 'json',
    }) as unknown as Observable<string>;
  }

  importAnnualSalaries(file: File, allowInactive = false): Observable<string> {
    const formData = new FormData();
    formData.append('file', file, file.name);
    return this.http.post(`${environment.apiUrl}/import-data/import-annual-salaries-file`, formData, {
      params: { allowInactive },
      responseType: 'text' as 'json',
    }) as unknown as Observable<string>;
  }

  importMonthlySalaries(file: File, allowInactive = false): Observable<string> {
    const formData = new FormData();
    formData.append('file', file, file.name);
    return this.http.post(`${environment.apiUrl}/import-data/import-monthly-salaries-file`, formData, {
      params: { allowInactive },
      responseType: 'text' as 'json',
    }) as unknown as Observable<string>;
  }

  getCurrentSalaries(employeeIds: number[]): Observable<EmployeeCurrentSalary[]> {
    return this.http.post<EmployeeCurrentSalary[]>(`${this.defaultUrl}/current-salaries`, employeeIds);
  }

  getEmployeesMini(filter: any = {}): Observable<PaginationResult<EmployeeMini[]>> {
    return this.http
      .get<PaginationResult<EmployeeMini[]>>(`${this.defaultUrl}/search-with-criteria`, { params: filter })
      .pipe(
        map((value) => {
          value.content = value.content.map((v: any) => ({
            ...v,
            completenessScore: Math.round(Number(v.completenessScore) || 0),
            activeContract: this.getActiveContract([
              ...this.normalizeToArray(v.permanentContractDetails),
              ...this.normalizeToArray(v.subContractorContractDetails),
              ...this.normalizeToArray(v.entryLevelContractDetails),
            ]),
          }));
          return value;
        }),
      );
  }

  getEmployees(filter: any = {}): Observable<PaginationResult<Employee[]>> {
    return this.http
      .get<PaginationResult<Employee[]>>(`${this.defaultUrl}/search-with-criteria`, { params: filter })
      .pipe(
        map((value) => {
          value.content = value.content.map((v: any) => ({
            ...v,
            completenessScore: Math.round(Number(v.completenessScore) || 0),
            activeContract: this.getActiveContract([
              ...this.normalizeToArray(v.permanentContractDetails),
              ...this.normalizeToArray(v.subContractorContractDetails),
              ...this.normalizeToArray(v.entryLevelContractDetails),
            ]),
          }));
          return value;
        }),
      );
  }

  getManagers(practice?: string): Observable<EmployeeMini[]> {
  let params = new HttpParams();
  if (practice) params = params.set('practice', practice);
  return this.http.get<EmployeeMini[]>(`${this.defaultUrl}/managers`, { params });
}

searchManagers(keyword: string, practice?: string): Observable<EmployeeMini[]> {
  let params = new HttpParams().set('keyword', keyword);
  if (practice) params = params.set('practice', practice);
  return this.http.get<EmployeeMini[]>(`${this.defaultUrl}/search/managers`, { params });
}

  getTechnicalInterviewers(employeeId: number): Observable<EmployeeMini[]> {
    return this.http.get<EmployeeMini[]>(`${this.defaultUrl}/technical-interviewers`, {
      params: { employeeId },
    });
  }

  getPromotionInterviewers(filter: any = {}): Observable<EmployeeMini[]> {
    return this.http.get<EmployeeMini[]>(`${this.defaultUrl}/interviewers`, { params: filter });
  }

  getAssignableEmployees(filter: any = {}): Observable<PaginationResult<AssignableEmployee[]>> {
    return this.http.get<PaginationResult<AssignableEmployee[]>>(`${this.defaultUrl}/assignable`, { params: filter });
  }

  getEvaluationEmployees(): Observable<any> {
    return this.http.get<any>(`${this.defaultUrl}/evaluation-employees`);
  }

  getLoggedInEmployee(corporateEmail: string): Observable<EmployeeMini> {
    return this.http.get<EmployeeMini>(`${this.defaultUrl}/logged-in`, { params: { corporateEmail } });
  }

  getBsaEmployeesForDropdown(page: number = 1, size: number = 1000): Observable<PaginationResult<EmployeeMini[]>> {
    const params = new HttpParams().set('practice', 'BSA').set('page', page.toString()).set('size', size.toString());

    return this.http.get<PaginationResult<EmployeeMini[]>>(`${this.defaultUrl}/search-with-criteria`, { params }).pipe(
      map((value) => {
        value.content = value.content.map((v: any) => ({
          ...v,
          completenessScore: Math.round(Number(v.completenessScore) || 0),
          activeContract: this.getActiveContract([
            ...this.normalizeToArray(v.permanentContractDetails),
            ...this.normalizeToArray(v.subContractorContractDetails),
            ...this.normalizeToArray(v.entryLevelContractDetails),
          ]),
        }));
        return value;
      }),
    );
  }

  searchEmployeeWithKeyword(filter: any = {}): Observable<PaginationResult<Employee[]>> {
    return this.http
      .get<PaginationResult<Employee[]>>(`${this.defaultUrl}/search-with-keyword`, { params: filter })
      .pipe(
        map((value) => {
          value.content = value.content.map((v: any) => ({
            ...v,
            completenessScore: Math.round(Number(v.completenessScore) || 0),
            activeContract: this.getActiveContract([
              ...this.normalizeToArray(v.permanentContractDetails),
              ...this.normalizeToArray(v.subContractorContractDetails),
              ...this.normalizeToArray(v.entryLevelContractDetails),
            ]),
          }));
          return value;
        }),
      );
  }

  getEmployeeByEmployeeNumber(employeeNumber: string): Observable<Employee> {
    return this.http.get<Employee>(`${this.defaultUrl}/${employeeNumber}`).pipe(
      map((value) => {
        value.childrenDetails = value.childrenDetails || [];
        value.childrenDetails.forEach((value1: any) => {
          value1.birthDateChild = moment(value1.birthDateChild).toDate();
        });
        return value;
      }),
    );
  }

  saveEmployee(employee: Employee): Observable<Employee> {
    return this.http.post<Employee>(`${this.defaultUrl}`, employee);
  }

  updateEmployee(id: number, employee: Employee): Observable<Employee> {
    return this.http.put<Employee>(`${this.defaultUrl}/${id}`, employee);
  }

  isImageUrlLoadable(url: string): Promise<string> {
    return new Promise((resolve) => {
      if (!url || url.length === 0) {
        resolve(this.defaultImageUrl);
        return;
      }
      this.http
        .get(url, { responseType: 'blob' })
        .pipe(take(1))
        .subscribe({
          next: () => resolve(url),
          error: () => resolve(this.defaultImageUrl),
        });
    });
  }

  archiveOrUnarchiveEmployee(employeeId: number, archive: boolean): Observable<any> {
    const params = new HttpParams().set('archive', archive.toString());
    return this.http.put(`${this.defaultUrl}/${employeeId}/archive`, {}, { params });
  }

  getContractsByEmployeeId(employeeId: number): Observable<any> {
    return this.http.get<any>(`${this.defaultUrl}/${employeeId}/contracts`);
  }

  getEmployeeById(employeeId: number): Observable<any> {
    return this.http.get<any>(`${this.defaultUrl}/${employeeId}/searchId`);
  }

  getLocationsByEmployeeId(employeeId: number): Observable<any> {
    return this.http.get<any>(`${this.defaultUrl}/${employeeId}/locations`);
  }

  getAssignmentsByEmployeeId(employeeId: number): Observable<any> {
    return this.http.get<any>(`${this.defaultUrl}/${employeeId}/assignments`);
  }

  getEmployeeWorklog(employeeId: number): Observable<EmployeeWorklog> {
    return this.http.get<EmployeeWorklog>(`${this.defaultUrl}/${employeeId}/worklog`);
  }

  assignEmployeeWorklogGroup(employeeId: number, groupId: number): Observable<EmployeeWorklog> {
    return this.http.put<EmployeeWorklog>(`${this.defaultUrl}/${employeeId}/worklog/group/${groupId}`, {});
  }

  getResponsible(): Observable<EmployeeMini[]> {
    return this.http.get<EmployeeMini[]>(`${this.defaultUrl}/pool-responsible`);
  }

  getResponsiblesForDropdown(categories?: string[]): Observable<EmployeeMini[]> {
    let params = new HttpParams();
    if (categories && categories.length) {
      categories.forEach((c) => (params = params.append('categories', c)));
    }
    return this.http.get<EmployeeMini[]>(`${this.defaultUrl}/responsibles`, { params });
  }

  findCampaignAdministrators(
    filter: { keyword?: string; page?: number; size?: number } = {},
  ): Observable<PaginationResult<EmployeeMini[]>> {
    const params: any = { page: filter.page ?? 0, size: filter.size ?? 20 };
    if (filter.keyword) params.keyword = filter.keyword;
    return this.http.get<PaginationResult<EmployeeMini[]>>(`${this.defaultUrl}/campaign-administrators`, { params });
  }

  getSupervisors(practice?: string): Observable<EmployeeMini[]> {
    let params = new HttpParams();

    if (practice?.trim()) {
      params = params.set('practice', practice.trim());
    }

    return this.http.get<EmployeeMini[]>(
      `${this.defaultUrl}/supervisors`,
      { params }
    );
  }

  getResponsiblesByProjectId(projectId: number): Observable<EmployeeMini[]> {
    return this.http.get<EmployeeMini[]>(`${this.defaultUrl}/responsible/project/${projectId}`);
  }

  getEmployeesByClientAndProject(clientId: number): Observable<Employee[]> {
    const params = new HttpParams().set('clientId', clientId.toString());
    return this.http.get<Employee[]>(`${this.defaultUrl}/by-client-project`, { params });
  }

  updateInterviewer(promotion: Promotion): Observable<Promotion> {
    return this.http.put<Promotion>(`${this.defaultUrl}/update-interviewer/${promotion.id}`, promotion);
  }

  getPromotionInterview(promotionId: number): Observable<PromotionInterview> {
    return this.http.get<PromotionInterview>(`${this.defaultUrl}/interview`, { params: { promotionId } });
  }

  get10Employees(): Observable<AssignableEmployee[]> {
    return this.http.get<AssignableEmployee[]>(`${this.defaultUrl}/getDropdownEmployees`);
  }

  getMiniEmployees(practice?: string): Observable<EmployeeMini[]> {
    let params = new HttpParams();
    if (practice) params = params.set('practice', practice);
    return this.http.get<EmployeeMini[]>(`${this.defaultUrl}/getDropdownMiniEmployees`, { params });
  }

  /**
   * Eligible Manager / Committer / Evaluator pool for a reviewee's practice:
   * supervisors of that practice + BSA practice + the campaign's Evaluation
   * Administrators.
   */
  getEligibleReviewers(practice?: string | null, campaignId?: number | null): Observable<EmployeeMini[]> {
    let params = new HttpParams();
    if (practice) params = params.set('practice', practice);
    if (campaignId != null) params = params.set('campaignId', String(campaignId));
    return this.http.get<EmployeeMini[]>(`${this.defaultUrl}/eligible-reviewers`, { params });
  }

  /**
   * Evaluation Administrators declared on a campaign. Defaults to the active
   * campaign when no id is given.
   */
  getEvaluationAdministrators(campaignId?: number | null): Observable<EmployeeMini[]> {
    let params = new HttpParams();
    if (campaignId != null) params = params.set('campaignId', String(campaignId));
    return this.http.get<EmployeeMini[]>(`${this.defaultUrl}/evaluation-administrators`, { params });
  }

  /**
   * Server-paginated people without assignment (Staff > People without
   * assignment). `filter` supports 0-indexed `page`, `size`,
   * `sort: 'field,asc|desc'` (sortable: employeeNumber, firstName, lastName,
   * fullName), `practices[]`, `employeeNum` and `fullName` (contains).
   */
  getUnassignedEmployees(filter: any = {}): Observable<PaginationResult<EmployeeMini[]>> {
    return this.http
      .get<PaginationResult<EmployeeMini[]>>(`${this.defaultUrl}/unassigned`, { params: filter })
      .pipe(map((value) => this.withActiveContract(value)));
  }

  /**
   * Server-paginated people without supervisors (Staff > People without
   * supervisors). Same filter contract as {@link getUnassignedEmployees}.
   */
  getEmployeesWithoutSupervisors(filter: any = {}): Observable<PaginationResult<EmployeeMini[]>> {
    return this.http
      .get<PaginationResult<EmployeeMini[]>>(`${this.defaultUrl}/without-supervisors`, { params: filter })
      .pipe(map((value) => this.withActiveContract(value)));
  }

  /**
   * Practice scope of the current user on a Staff page: STAFF_LIST (Staff list) or
   * ATTENTION (People without assignment, People without supervisors). The backend
   * enforces it; the UI only uses it to set up the practice selector.
   */
  getStaffVisibility(view: StaffView): Observable<StaffVisibility> {
    return this.http.get<StaffVisibility>(`${this.defaultUrl}/staff-visibility`, { params: { view } });
  }

  private withActiveContract(value: PaginationResult<EmployeeMini[]>): PaginationResult<EmployeeMini[]> {
    value.content = (value.content ?? []).map((v: any) => ({
      ...v,
      completenessScore: Math.round(Number(v.completenessScore) || 0),
      activeContract: this.getActiveContract([
        ...this.normalizeToArray(v.permanentContractDetails),
        ...this.normalizeToArray(v.subContractorContractDetails),
        ...this.normalizeToArray(v.entryLevelContractDetails),
      ]),
    }));
    return value;
  }

  getLightEmployeeList(): Observable<EmployeeMini[]> {
    return this.http.get<EmployeeMini[]>(`${this.defaultUrl}/lightEmployeeList`, {});
  }

  getMiniEmployeesForPromotion(): Observable<EmployeeMini[]> {
    return this.http.get<EmployeeMini[]>(`${this.defaultUrl}/getDropdownMiniEmployeesForPromotion`);
  }

  searchEmployees(keyword: string): Observable<AssignableEmployee[]> {
    const params = new HttpParams().set('keyword', keyword);
    return this.http.get<AssignableEmployee[]>(`${this.defaultUrl}/search`, { params });
  }

  searchMiniEmployees(keyword: string, practice?: string): Observable<EmployeeMini[]> {
    let params = new HttpParams().set('keyword', keyword);
    if (practice) params = params.set('practice', practice);
    return this.http.get<EmployeeMini[]>(`${this.defaultUrl}/search-employees`, { params });
  }

  searchOpportunityEmployees(keyword: string, practice?: string): Observable<EmployeeMini[]> {
    let params = new HttpParams().set('keyword', keyword);
    if (practice) params = params.set('practice', practice);
    return this.http.get<EmployeeMini[]>(`${this.defaultUrl}/opportunity-search`, { params });
  }
  searchMiniEmployeesForPromotion(keyword: string): Observable<EmployeeMini[]> {
    const params = new HttpParams().set('keyword', keyword);

    return this.http.get<EmployeeMini[]>(`${this.defaultUrl}/search-employees-for-promotion`, { params });
  }
  getEmployeesByProjectLeadId(leadId: number): Observable<Employee[]> {
    const params = new HttpParams().set('leadId', String(leadId));
    return this.http.get<Employee[]>(`${this.defaultUrl}/by-project-lead`, { params });
  }

  getEmployeesByProjectLeadEmail(corporateEmail: string): Observable<Employee[]> {
    const params = new HttpParams().set('corporateEmail', corporateEmail);
    return this.http.get<Employee[]>(`${this.defaultUrl}/by-project-lead`, { params });
  }

  getEmployeesByProjectLead(leadId?: number, corporateEmail?: string): Observable<Employee[]> {
    if (leadId != null) return this.getEmployeesByProjectLeadId(leadId);
    if (corporateEmail) return this.getEmployeesByProjectLeadEmail(corporateEmail);
    // backend supports both params optional and returns [] if neither provided
    return this.http.get<Employee[]>(`${this.defaultUrl}/by-project-lead`);
  }

  getEmployeesAssignedToReviewer(reviewerId?: number, corporateEmail?: string): Observable<Employee[]> {
    let params = new HttpParams();
    if (reviewerId != null) {
      params = params.set('reviewerId', reviewerId.toString());
    }
    if (corporateEmail) {
      params = params.set('corporateEmail', corporateEmail);
    }
    return this.http.get<Employee[]>(`${this.defaultUrl}/assigned-to-reviewer`, { params });
  }

  private normalizeToArray<T = any>(x: T | T[] | null | undefined): T[] {
    if (!x) return [];
    return Array.isArray(x) ? x : [x];
  }

  private getActiveContract(contracts: any[] | undefined): any | undefined {
    if (!contracts || contracts.length === 0) return undefined;
    const candidates = contracts.filter(Boolean);
    if (candidates.length === 0) return undefined;
    const active = candidates.find((c) => c && c.active === true);
    return active || candidates[0];
  }
  exportEmployeesList(filters: any) {
    const params: any = { ...filters, mode: 'LIST' };
    return this.http.get(`${this.defaultUrl}/export`, {
      params,
      responseType: 'blob',
    });
  }

  exportEducationGrant(filters: any) {
    const params: any = { ...filters, mode: 'EDUCATION_GRANT' };
    return this.http.get(`${this.defaultUrl}/export`, {
      params,
      responseType: 'blob',
    });
  }

  hasUnreviewedAiJobs(employeeIds: number[]): Observable<boolean> {
    return this.http.get<boolean>(`${environment.apiUrl}/documents/0/ai-extraction/has-unreviewed`, {
      params: { employeeIds: employeeIds.join(',') },
    });
  }

  exportEmployeesCard(filters: any) {
    const params: any = { ...filters, mode: 'CARD' };
    return this.http.get(`${this.defaultUrl}/export`, {
      params,
      responseType: 'blob',
    });
  }

  exportEducationGrantEmployees(filters: any) {
    const params: any = { ...filters, mode: 'EDUCATION_GRANT' };
    return this.http.get(`${this.defaultUrl}/export`, {
      params,
      responseType: 'blob',
    });
  }

  exportRibChanges(filters: any) {
    const params: any = { ...filters, mode: 'RIB_CHANGES' };
    return this.http.get(`${this.defaultUrl}/export`, {
      params,
      responseType: 'blob',
    });
  }

  exportMissingDocuments(filters: any) {
    const params: any = { ...filters, mode: 'MISSING_DOCUMENTS' };
    return this.http.get(`${this.defaultUrl}/export`, {
      params,
      responseType: 'blob',
    });
  }

  exportSeniorityMilestones(filters: any) {
    const params: any = { ...filters, mode: 'SENIORITY_MILESTONES' };
    return this.http.get(`${this.defaultUrl}/export`, {
      params,
      responseType: 'blob',
    });
  }

  /**
   * Director report export.
   * Passing `{ practice: 'APPS' }` scopes the report to that practice; the
   * backend ignores an unknown/absent practice and returns every employee.
   */
  exportDirectorEmployees(filters: any) {
    const params: any = { ...filters, mode: 'DIRECTOR' };
    return this.http.get(`${this.defaultUrl}/export`, {
      params,
      responseType: 'blob',
    });
  }

  exportStaffingFile(filters: any) {
    const params: any = { ...filters, mode: 'STAFFING' };
    return this.http.get(`${this.defaultUrl}/export`, {
      params,
      responseType: 'blob',
    });
  }

  findEmployeesForEvaluationPermissions(keyword?: string): Observable<EmployeeMini[]> {
    return this.http.get<EmployeeMini[]>(`${this.defaultUrl}/evaluation-permissions-employees`, {
      params: keyword ? { keyword } : {},
    });
  }

  getProfileImage(profileImageUrl: string): Observable<Blob> {
    return this.http.get(profileImageUrl, {
      responseType: 'blob',
    });
  }

  getProjectLeads(): Observable<Employee[]> {
    return this.http.get<Employee[]>(`${this.defaultUrl}/project-leads`);
  }

  getEligibleResponsibles(): Observable<EmployeeMini[]> {
    return this.http.get<EmployeeMini[]>(`${this.defaultUrl}/eligible-responsibles`); // <--- Beddel had le path
  }
}
